import { useEffect, useRef, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, ArrowPathIcon, DocumentTextIcon, ClipboardDocumentIcon, KeyIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { Modal, ConfirmDialog, PageLoader, EmptyState, StatusBadge } from '@/components/common';
import { organizationsApi, apiKeysApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import NotificationConfigEditor, { type NotificationConfigEditorHandle } from '@/components/cms/NotificationConfigEditor';
import Gk2ControlConfigEditor, { type Gk2ControlConfigEditorHandle } from '@/components/cms/Gk2ControlConfigEditor';
import type { Organization } from '@/types';
import { PERMISSIONS } from '@/types';
import PublicInfoEditor from '@/components/organizations/PublicInfoEditor';

/**
 * Public announcements endpoint (web-publish channel) for a given org slug.
 * Built against the current window origin: dashboard and API share the same
 * public origin via reverse proxy. Reading the Vite injected variable at
 * runtime is unsafe — bracket access is undefined in production builds.
 */
function announcementsEndpoint(slug: string): string {
  return `${window.location.origin}/api/public/marketing/${slug}/announcements`;
}

export default function OrganizationsPage() {
  const { hasPermission, hasPlatformPermission } = useAuthStore();
  // Equipo técnico VIDA (VIDA-347): da de alta y configura cualquier org, pero no
  // la elimina ni reinicia sus BD (eso sigue siendo solo de super_admin).
  const canManagePlatformOrgs = hasPlatformPermission(PERMISSIONS.PLATFORM_ORGS);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  // Refs para disparar el guardado de los sub-editores (notificaciones + gk2)
  // desde el boton "Guardar" del footer del modal de editar org.
  const notifRef = useRef<NotificationConfigEditorHandle | null>(null);
  const gk2Ref = useRef<Gk2ControlConfigEditorHandle | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployInfo, setDeployInfo] = useState<{
    org: { _id: string; name: string; slug: string; database_name: string };
    vida_pet: string;
    ecommerce: string;
    catalogo: string;
    api_url: string;
  } | null>(null);
  const [deployTab, setDeployTab] = useState<'pet' | 'ecommerce' | 'catalogo'>('pet');
  const [deployLoading, setDeployLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    domain: '',
    welcome_text: '',
    allowed_origins: [] as string[],
    ecommerce: false,
    catalog: false,
    blog: false,
    multilingual: true,
    analytics: true,
    planner: false,
    processes: false,
  });

  // Texto del input de "añadir origen". Vive aparte de formData porque es un
  // borrador: solo pasa a la lista cuando se valida y se guarda.
  const [originDraft, setOriginDraft] = useState('');

  // Ecommerce DB info state
  const [ecommerceDbInfo, setEcommerceDbInfo] = useState<{
    dbName: string;
    exists: boolean;
    collections?: number;
    customersDbName?: string;
    customersExists?: boolean;
    customersCollections?: number;
    customerCount?: number;
  } | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [initLoading, setInitLoading] = useState<string | null>(null);

  // API Keys state
  const [apiKeysOrg, setApiKeysOrg] = useState<Organization | null>(null);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newKeyForm, setNewKeyForm] = useState({ name: '', service: 'pet', rate_limit: 500 });
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeySecret, setNewKeySecret] = useState<string | null>(null);
  const [regeneratingKeyId, setRegeneratingKeyId] = useState<string | null>(null);
  // A qué key pertenece el secreto revelado (para mostrarlo junto a su fila en la ficha de despliegue)
  const [newKeySecretId, setNewKeySecretId] = useState<string | null>(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = canManagePlatformOrgs
        ? await organizationsApi.list()
        : await organizationsApi.my();
      setOrganizations(response.data.organizations);
    } catch (error) {
      toast.error('Error al cargar organizaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.slug) {
      toast.error('Nombre y slug son requeridos');
      return;
    }

    try {
      const res = await organizationsApi.create({
        name: formData.name,
        slug: formData.slug,
        config: {
          ...(formData.domain ? { domain: formData.domain } : {}),
          features: {
            ecommerce: formData.ecommerce,
            catalog: formData.catalog,
          },
        },
      });
      const newOrg = res.data.organization;

      // Auto-create API keys for enabled services
      const keyServices: Array<{ service: string; label: string }> = [
        { service: 'pet', label: 'PET-VIDA' },
        ...(formData.ecommerce ? [{ service: 'ecommerce', label: 'Ecommerce' }] : []),
        ...(formData.catalog ? [{ service: 'catalog', label: 'Catálogo' }] : []),
      ];
      await Promise.allSettled(
        keyServices.map(({ service, label }) =>
          apiKeysApi.createForOrg(newOrg._id, { name: `${formData.name} ${label}`, service, rate_limit: 500 })
        )
      );

      toast.success('Organización creada con API keys generadas');
      setShowCreateModal(false);
      setFormData({ name: '', slug: '', domain: '', welcome_text: '', allowed_origins: [], ecommerce: false, catalog: false, blog: false, multilingual: true, analytics: true, planner: false, processes: false });
      loadOrganizations();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al crear';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!selectedOrg) return;

    setDeleteLoading(true);
    try {
      await organizationsApi.delete(selectedOrg._id);
      toast.success('Organización eliminada');
      setShowDeleteConfirm(false);
      setSelectedOrg(null);
      loadOrganizations();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al eliminar';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const openViewModal = async (org: Organization) => {
    setSelectedOrg(org);
    setApiKeysOrg(org);
    setNewKeySecret(null);
    setNewKeyForm({ name: '', service: 'pet', rate_limit: 500 });
    setShowViewModal(true);
    setApiKeysLoading(true);
    try {
      const res = await apiKeysApi.listForOrg(org._id);
      setApiKeys(res.data.apiKeys || []);
    } catch {
      setApiKeys([]);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const openEditModal = async (org: Organization) => {
    setSelectedOrg(org);
    setOriginDraft('');
    setFormData({
      name: org.name,
      slug: org.slug,
      domain: org.config?.domain || '',
      welcome_text: org.config?.welcome_text || '',
      allowed_origins: org.config?.allowed_origins || [],
      ecommerce: org.config?.features?.ecommerce || false,
      catalog: org.config?.features?.catalog || false,
      blog: org.config?.features?.blog || false,
      multilingual: org.config?.features?.multilingual ?? true,
      analytics: org.config?.features?.analytics ?? true,
      planner: org.config?.features?.planner || false,
      processes: org.config?.features?.processes || false,
    });

    if (org.config?.features?.ecommerce) {
      try {
        const response = await organizationsApi.getEcommerceDb(org._id);
        setEcommerceDbInfo(response.data);
      } catch {
        setEcommerceDbInfo(null);
      }
    } else {
      setEcommerceDbInfo(null);
    }

    setShowEditModal(true);
  };

  const handleEdit = async () => {
    if (!selectedOrg) return;

    if (formData.name === '') {
      toast.error('El nombre es requerido');
      return;
    }

    setEditLoading(true);
    try {
      await organizationsApi.update(selectedOrg._id, {
        name: formData.name,
        config: {
          ...selectedOrg.config,
          domain: formData.domain || undefined,
          features: {
            ...selectedOrg.config?.features,
            ecommerce: formData.ecommerce,
            catalog: formData.catalog,
          },
        },
      });

      const prevEcommerce = selectedOrg.config?.features?.ecommerce || false;
      const prevCatalog = selectedOrg.config?.features?.catalog || false;

      // Create DB and API key when a service is newly activated
      const newlyActivated: Array<{ service: string; label: string }> = [];
      if (!prevEcommerce && formData.ecommerce) newlyActivated.push({ service: 'ecommerce', label: 'Ecommerce' });
      if (!prevCatalog && formData.catalog) newlyActivated.push({ service: 'catalog', label: 'Catálogo' });

      if (formData.ecommerce) {
        try {
          await organizationsApi.createEcommerceDb(selectedOrg._id);
        } catch {
          // DB may already exist
        }
      }

      if (newlyActivated.length > 0) {
        await Promise.allSettled(
          newlyActivated.map(({ service, label }) =>
            apiKeysApi.createForOrg(selectedOrg._id, { name: `${selectedOrg.name} ${label}`, service, rate_limit: 500 })
          )
        );
        toast.success(`Servicio(s) activado(s) con API key generada: ${newlyActivated.map(s => s.label).join(', ')}`);
      } else {
        toast.success('Organización actualizada');
      }

      setShowEditModal(false);
      setSelectedOrg(null);
      setFormData({ name: '', slug: '', domain: '', welcome_text: '', allowed_origins: [], ecommerce: false, catalog: false, blog: false, multilingual: true, analytics: true, planner: false, processes: false });
      loadOrganizations();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al actualizar';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleResetEcommerceDb = async () => {
    if (!selectedOrg) return;

    const confirm = window.confirm('¿Estás seguro? Esto eliminará todos los productos y pedidos de la tienda.');
    if (!confirm) return;

    try {
      await organizationsApi.resetEcommerceDb(selectedOrg._id, true);
      toast.success('Base de datos reiniciada');
      loadOrganizations();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al reiniciar BD';
      toast.error(message);
    }
  };

  const handleSyncCustomers = async () => {
    if (!selectedOrg) return;

    setSyncLoading(true);
    try {
      await organizationsApi.syncCustomers(selectedOrg._id);
      toast.success('Clientes sincronizados correctamente');

      const response = await organizationsApi.getEcommerceDb(selectedOrg._id);
      setEcommerceDbInfo(response.data);
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al sincronizar';
      toast.error(message);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleInitialize = async (org: Organization) => {
    setInitLoading(org._id);
    try {
      const res = await organizationsApi.initialize(org._id);
      const { steps, errors } = res.data;
      if (errors.length > 0) {
        toast.error(`Completado con errores: ${errors.join(', ')}`);
      } else {
        toast.success(`Inicializada: ${steps.join(' · ')}`);
      }
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al inicializar';
      toast.error(message);
    } finally {
      setInitLoading(null);
    }
  };

  const handleShowDeployInfo = async (org: Organization) => {
    setDeployLoading(true);
    setDeployTab('pet');
    setApiKeysOrg(org);
    setNewKeySecret(null);
    setNewKeySecretId(null);
    setShowDeployModal(true);
    try {
      const [deployRes, keysRes] = await Promise.all([
        organizationsApi.getDeploymentInfo(org._id),
        apiKeysApi.listForOrg(org._id),
      ]);
      setDeployInfo(deployRes.data);
      setApiKeys(keysRes.data.apiKeys || []);
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al obtener ficha de despliegue';
      toast.error(message);
      setShowDeployModal(false);
    } finally {
      setDeployLoading(false);
    }
  };

const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKeysOrg || !newKeyForm.name) return;
    setCreatingKey(true);
    setNewKeySecret(null);
    try {
      const res = await apiKeysApi.createForOrg(apiKeysOrg._id, newKeyForm);
      setNewKeySecret(res.data.apiKey.key);
      setApiKeys(prev => [...prev, res.data.apiKey]);
      setNewKeyForm({ name: '', service: 'pet', rate_limit: 500 });
      toast.success('API key creada');
    } catch {
      toast.error('Error al crear API key');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeApiKey = async (keyId: string) => {
    if (!apiKeysOrg) return;
    try {
      await apiKeysApi.revokeForOrg(apiKeysOrg._id, keyId);
      setApiKeys(prev => prev.filter(k => k._id !== keyId));
      toast.success('API key revocada');
    } catch {
      toast.error('Error al revocar API key');
    }
  };

  // La key solo existe en claro al generarla (en BD se guarda su hash). Si se
  // pierde, la única vía es regenerarla: la anterior deja de funcionar al instante.
  const handleRegenerateApiKey = async (keyId: string, keyName: string) => {
    if (!apiKeysOrg) return;
    const ok = confirm(
      `¿Regenerar la API key "${keyName}"?\n\n` +
      'La clave actual dejará de funcionar INMEDIATAMENTE. Tendrás que copiar la nueva ' +
      'y actualizarla donde la uses (p. ej. VIDA_API_KEY en Vercel) y redesplegar.'
    );
    if (!ok) return;
    setRegeneratingKeyId(keyId);
    setNewKeySecret(null);
    setNewKeySecretId(null);
    try {
      const res = await apiKeysApi.regenerateForOrg(apiKeysOrg._id, keyId);
      const updated = res.data.apiKey;
      setNewKeySecret(updated.key);
      setNewKeySecretId(keyId);
      setApiKeys(prev => prev.map(k => (k._id === keyId ? { ...k, ...updated, key: undefined } : k)));
      toast.success('API key regenerada — cópiala ahora');
    } catch {
      toast.error('Error al regenerar API key');
    } finally {
      setRegeneratingKeyId(null);
    }
  };

  /**
   * Bloque de API keys de un servicio dentro de la FICHA DE DESPLIEGUE.
   * Muestra el prefijo (la clave no puede recuperarse: en BD solo hay su hash) y
   * permite regenerarla, revelando el nuevo valor una única vez junto a su fila.
   */
  const renderDeployApiKeys = (service: string, label: string) => {
    const keys = apiKeys.filter((k: any) => k.service === service);
    if (keys.length === 0) {
      return (
        <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
          Sin API key para <strong>{label}</strong> — créala en "Ver detalles"
        </p>
      );
    }
    return (
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-xs font-semibold text-yellow-800 mb-2 flex items-center gap-1">
          <KeyIcon className="w-3.5 h-3.5" /> API Keys — {label}
        </p>
        {keys.map((k: any) => (
          <div key={k._id} className="mb-2 last:mb-0">
            <div className="flex items-center gap-2">
              <span className="text-xs text-yellow-700 font-medium w-28 truncate">{k.name}</span>
              <code className="flex-1 text-xs font-mono text-yellow-900">{k.key_prefix}•••</code>
              <button
                onClick={() => handleRegenerateApiKey(k._id, k.name)}
                disabled={regeneratingKeyId === k._id}
                className="flex-shrink-0 text-xs font-medium text-yellow-800 hover:text-yellow-950 underline disabled:opacity-50"
                title="Genera una clave nueva y la muestra una vez. La anterior deja de funcionar al instante."
              >
                {regeneratingKeyId === k._id ? 'Regenerando…' : 'Regenerar'}
              </button>
            </div>
            {newKeySecretId === k._id && newKeySecret && (
              <div className="mt-1 p-2 bg-white border border-yellow-300 rounded">
                <p className="text-[11px] font-semibold text-yellow-800 mb-1">
                  Cópiala ahora — no se volverá a mostrar (en BD solo se guarda su hash)
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono break-all text-yellow-900">{newKeySecret}</code>
                  <button
                    onClick={() => copyToClipboard(newKeySecret)}
                    className="flex-shrink-0 p-1 text-yellow-700 hover:text-yellow-900"
                    title="Copiar"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado al portapapeles');
    } catch {
      toast.error('Error al copiar');
    }
  };

  const resetEditModal = () => {
    setShowEditModal(false);
    setSelectedOrg(null);
    setFormData({ name: '', slug: '', domain: '', welcome_text: '', allowed_origins: [], ecommerce: false, catalog: false, blog: false, multilingual: true, analytics: true, planner: false, processes: false });
    setEcommerceDbInfo(null);
  };

  // Generic toggle handler for "simple" feature flags (no DB side-effects).
  // Used by blog, multilingual, analytics, planner. Ecommerce/catalog have
  // their own bespoke handlers because they create/use tenant DBs on activation.
  const toggleFeatureFlag = async (
    featureKey: 'blog' | 'multilingual' | 'analytics' | 'planner' | 'processes',
    label: string,
  ) => {
    if (!selectedOrg) return;
    const newValue = !formData[featureKey];
    setFormData({ ...formData, [featureKey]: newValue });
    setEditLoading(true);
    try {
      await organizationsApi.update(selectedOrg._id, {
        name: formData.name,
        config: {
          features: {
            ...(selectedOrg.config?.features || {}),
            [featureKey]: newValue,
          },
        },
      });
      toast.success(`${label} ${newValue ? 'activado' : 'desactivado'}`);
      loadOrganizations();
      const response = await organizationsApi.get(selectedOrg._id);
      setSelectedOrg(response.data.organization);
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
      toast.error(message);
      setFormData({ ...formData, [featureKey]: !newValue });
    } finally {
      setEditLoading(false);
    }
  };

  // Toggle del bloque Gestion (Asociados/Cuotas/Presupuesto). A diferencia de
  // las features de arriba, la convencion aqui es: undefined → habilitado.
  // Para desactivar enviamos enabled:false explicito; para reactivar enviamos
  // true. El backend `requireManagementFeature` aplica la misma politica.
  const toggleManagementFlag = async (
    submodule: 'members' | 'fees' | 'budget' | 'meetings' | 'crm',
    label: string,
    nextEnabled: boolean,
  ) => {
    if (!selectedOrg) return;
    setEditLoading(true);
    try {
      await organizationsApi.update(selectedOrg._id, {
        config: {
          management: {
            ...(selectedOrg.config?.management || {}),
            [submodule]: {
              ...(selectedOrg.config?.management?.[submodule] || {}),
              enabled: nextEnabled,
            },
          },
        },
      });
      toast.success(`${label} ${nextEnabled ? 'activado' : 'desactivado'}`);
      loadOrganizations();
      const response = await organizationsApi.get(selectedOrg._id);
      setSelectedOrg(response.data.organization);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  // Autofichaje (VIDA-256): opt-in por organización. Se envía el objeto completo
  // porque el backend sustituye `config.presence` entero al guardar.
  const togglePresenceFlag = async (
    key: 'self_clock_enabled' | 'self_clock_geolocation',
    label: string,
    nextEnabled: boolean,
  ) => {
    if (!selectedOrg) return;
    setEditLoading(true);
    try {
      await organizationsApi.update(selectedOrg._id, {
        config: { presence: { ...(selectedOrg.config?.presence || {}), [key]: nextEnabled } },
      });
      toast.success(`${label} ${nextEnabled ? 'activado' : 'desactivado'}`);
      loadOrganizations();
      const response = await organizationsApi.get(selectedOrg._id);
      setSelectedOrg(response.data.organization);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };
  const selfClockOn = selectedOrg?.config?.presence?.self_clock_enabled === true;
  const selfClockGeoOn = selectedOrg?.config?.presence?.self_clock_geolocation === true;

  // Helper: lee el estado actual del submodulo (undefined → true por defecto).
  const isManagementEnabled = (submodule: 'members' | 'fees' | 'budget'): boolean => {
    const flag = selectedOrg?.config?.management?.[submodule]?.enabled;
    return flag !== false;
  };
  // Reuniones es OPT-IN (al contrario que members/fees/budget): off por defecto.
  const isMeetingsEnabled = (): boolean =>
    selectedOrg?.config?.management?.meetings?.enabled === true;
  // CRM también es OPT-IN: off por defecto.
  const isCrmEnabled = (): boolean =>
    selectedOrg?.config?.management?.crm?.enabled === true;

  // Guarda un campo simple (name, domain) al perder foco — solo si cambio
  // respecto al valor original. Mismo patron que los toggles: cada cambio
  // persiste inmediatamente, sin necesidad de un boton "Guardar" global.
  const saveBasicField = async (
    field: 'name' | 'domain' | 'welcome_text',
    nextValue: string
  ): Promise<void> => {
    if (!selectedOrg) return;
    const current =
      field === 'name'
        ? selectedOrg.name
        : field === 'domain'
          ? (selectedOrg.config?.domain ?? '')
          : (selectedOrg.config?.welcome_text ?? '');
    if (nextValue === current) return;
    if (field === 'name' && !nextValue.trim()) {
      toast.error('El nombre no puede quedar vacío');
      // restaura visualmente
      setFormData({ ...formData, name: current });
      return;
    }
    setEditLoading(true);
    try {
      const payload: Record<string, unknown> = {};
      if (field === 'name') {
        payload.name = nextValue;
      } else if (field === 'domain') {
        payload.config = {
          ...selectedOrg.config,
          domain: nextValue || undefined,
        };
      } else {
        payload.config = {
          ...selectedOrg.config,
          welcome_text: nextValue,
        };
      }
      await organizationsApi.update(selectedOrg._id, payload);
      toast.success(
        field === 'name'
          ? 'Nombre actualizado'
          : field === 'domain'
            ? 'Dominio actualizado'
            : 'Mensaje de bienvenida actualizado'
      );
      loadOrganizations();
      const response = await organizationsApi.get(selectedOrg._id);
      setSelectedOrg(response.data.organization);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al guardar';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  // Mismo saneo que el backend, para que lo que se ve sea lo que se guarda.
  const normalizeOrigin = (raw: string) =>
    raw.trim().toLowerCase().replace(/\/+$/, '');

  // Un origen es esquema + host + puerto: ni path, ni query, ni barra final.
  // Validamos en cliente para dar el error en el sitio; el backend revalida igual.
  const isValidOrigin = (value: string) => {
    try {
      const url = new URL(value);
      return (
        (url.protocol === 'https:' || url.protocol === 'http:') &&
        url.origin.toLowerCase() === value
      );
    } catch {
      return false;
    }
  };

  const saveAllowedOrigins = async (next: string[]): Promise<boolean> => {
    if (!selectedOrg) return false;
    setEditLoading(true);
    try {
      await organizationsApi.update(selectedOrg._id, {
        config: {
          ...selectedOrg.config,
          allowed_origins: next,
        },
      });
      setFormData((prev) => ({ ...prev, allowed_origins: next }));
      toast.success('Orígenes actualizados');
      const response = await organizationsApi.get(selectedOrg._id);
      setSelectedOrg(response.data.organization);
      return true;
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Error al guardar los orígenes';
      toast.error(message);
      return false;
    } finally {
      setEditLoading(false);
    }
  };

  const addOrigin = async () => {
    const value = normalizeOrigin(originDraft);
    if (!value) return;
    if (!isValidOrigin(value)) {
      toast.error('Origen inválido. Formato: https://midominio.com (sin barra final ni ruta)');
      return;
    }
    if (formData.allowed_origins.includes(value)) {
      toast.error('Ese origen ya está en la lista');
      return;
    }
    const ok = await saveAllowedOrigins([...formData.allowed_origins, value]);
    if (ok) setOriginDraft('');
  };

  const removeOrigin = async (origin: string) => {
    await saveAllowedOrigins(formData.allowed_origins.filter((o) => o !== origin));
  };

  // Cambia el modo de transparencia del Presupuesto (full ↔ aggregated_only).
  // En aggregated_only los socios con BUDGET_READ ven KPIs pero no apuntes.
  const setBudgetTransparency = async (next: 'full' | 'aggregated_only') => {
    if (!selectedOrg) return;
    setEditLoading(true);
    try {
      await organizationsApi.update(selectedOrg._id, {
        config: {
          management: {
            ...(selectedOrg.config?.management || {}),
            budget: {
              ...(selectedOrg.config?.management?.budget || {}),
              transparency: next,
            },
          },
        },
      });
      toast.success(
        next === 'full'
          ? 'Modo transparente activado'
          : 'Modo agregado activado (los apuntes solo serán visibles a tesorería)'
      );
      loadOrganizations();
      const response = await organizationsApi.get(selectedOrg._id);
      setSelectedOrg(response.data.organization);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <Header
        title="Organizaciones"
        subtitle="Gestiona las webs y sus configuraciones"
        actions={
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <PlusIcon className="w-5 h-5 mr-2" />
            Nueva organización
          </button>
        }
      />

      <div className="p-6">
        {organizations.length === 0 ? (
          <EmptyState
            title="No hay organizaciones"
            description="Crea tu primera organización para empezar a gestionar webs"
            action={{
              label: 'Crear organización',
              onClick: () => setShowCreateModal(true),
            }}
          />
        ) : (
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Slug</th>
                    <th>Base de datos</th>
                    <th>Dominio</th>
                    <th>Estado</th>
                    <th>Creada</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {organizations.map((org) => (
                    <tr key={org._id}>
                      <td className="font-medium">{org.name}</td>
                      <td className="text-gray-500">{org.slug}</td>
                      <td className="font-mono text-sm text-gray-500">{org.database_name}</td>
                      <td className="text-gray-500">{org.config?.domain || '-'}</td>
                      <td>
                        <StatusBadge status={org.status} />
                      </td>
                      <td className="text-gray-500">
                        {new Date(org.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openViewModal(org)}
                            className="p-1 text-gray-400 hover:text-vida-600"
                            title="Ver detalles"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => openEditModal(org)}
                            className="p-1 text-gray-400 hover:text-vida-600"
                            title="Editar"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          {canManagePlatformOrgs && (
                            <>
                              <button
                                onClick={() => handleShowDeployInfo(org)}
                                className="p-1 text-gray-400 hover:text-blue-600"
                                title="Ficha de despliegue"
                              >
                                <DocumentTextIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleInitialize(org)}
                                disabled={initLoading === org._id}
                                className="p-1 text-gray-400 hover:text-green-600 disabled:opacity-40"
                                title="Inicializar infraestructura (DBs + bucket + secciones)"
                              >
                                <ArrowPathIcon className={`w-5 h-5 ${initLoading === org._id ? 'animate-spin' : ''}`} />
                              </button>
                              {hasPermission('*') && (
                                <button
                                  onClick={() => {
                                    setSelectedOrg(org);
                                    setShowDeleteConfirm(true);
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                  title="Eliminar"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Nueva organización"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="label">Nombre</label>
            <input
              type="text"
              className="input"
              placeholder="Mi Web"
              value={formData.name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  name: e.target.value,
                  slug: generateSlug(e.target.value),
                });
              }}
            />
          </div>

          <div>
            <label className="label">Slug (identificador único)</label>
            <input
              type="text"
              className="input font-mono"
              placeholder="mi-web"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">
              Base de datos: vida_{formData.slug.replace(/-/g, '_') || '...'}
            </p>
          </div>

          <div>
            <label className="label">Dominio (opcional)</label>
            <input
              type="text"
              className="input"
              placeholder="www.miweb.com"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
            />
          </div>

          <div className="border-t pt-4 mt-4 space-y-3">
            <p className="text-sm font-medium text-gray-700">Funcionalidades</p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Ecommerce</span>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, ecommerce: !formData.ecommerce })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.ecommerce ? 'bg-vida-600' : 'bg-gray-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.ecommerce ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Catalogo</span>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, catalog: !formData.catalog })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.catalog ? 'bg-vida-600' : 'bg-gray-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.catalog ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Crear organización
            </button>
          </div>
        </form>
      </Modal>

      {/* View modal */}
      <Modal
        open={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedOrg(null);
          setNewKeySecret(null);
        }}
        title="Detalles de la organización"
        size="lg"
      >
        {selectedOrg && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Nombre</label>
                <p className="text-gray-900">{selectedOrg.name}</p>
              </div>
              <div>
                <label className="label">Slug</label>
                <p className="font-mono text-gray-600">{selectedOrg.slug}</p>
              </div>
              <div>
                <label className="label">Base de datos</label>
                <p className="font-mono text-gray-600">{selectedOrg.database_name}</p>
              </div>
              <div>
                <label className="label">Dominio</label>
                <p className="text-gray-600">{selectedOrg.config?.domain || '-'}</p>
              </div>
              <div>
                <label className="label">Estado</label>
                <StatusBadge status={selectedOrg.status} />
              </div>
              <div>
                <label className="label">Productos activos</label>
                <div className="flex gap-1.5 flex-wrap mt-0.5">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">PET-VIDA / CMS</span>
                  {selectedOrg.config?.features?.ecommerce && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Ecommerce</span>
                  )}
                  {selectedOrg.config?.features?.catalog && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Catálogo</span>
                  )}
                </div>
              </div>
            </div>

            {/* API Keys section */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                <KeyIcon className="w-4 h-4 text-yellow-600" />
                <h3 className="text-sm font-semibold text-gray-900">API Keys</h3>
              </div>

              {/* One-time secret display */}
              {newKeySecret && (
                <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg mb-3">
                  <p className="text-xs font-semibold text-yellow-800 mb-1">Guarda esta clave ahora — no se volverá a mostrar</p>
                  <p className="text-[11px] text-yellow-700 mb-2">En la base de datos solo se guarda su hash, por eso no puede recuperarse. Si la pierdes, usa «Regenerar».</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs font-mono break-all text-yellow-900">{newKeySecret}</code>
                    <button
                      onClick={() => copyToClipboard(newKeySecret)}
                      className="flex-shrink-0 p-1 text-yellow-700 hover:text-yellow-900"
                      title="Copiar"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Create new key form */}
              <form onSubmit={handleCreateApiKey} className="flex gap-2 items-end flex-wrap border border-gray-200 rounded-lg p-3 bg-gray-50 mb-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Nombre</label>
                  <input
                    className="input text-sm py-1.5"
                    placeholder="ej: vida_pet producción"
                    value={newKeyForm.name}
                    onChange={e => setNewKeyForm(f => ({ ...f, name: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Servicio</label>
                  <select
                    className="input text-sm py-1.5"
                    value={newKeyForm.service}
                    onChange={e => setNewKeyForm(f => ({ ...f, service: e.target.value }))}
                  >
                    <option value="pet">pet (CMS web)</option>
                    {selectedOrg.config?.features?.ecommerce && <option value="ecommerce">ecommerce</option>}
                    {selectedOrg.config?.features?.catalog && <option value="catalog">catálogo</option>}
                    <option value="custom">custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Rate limit / 15 min</label>
                  <input
                    type="number"
                    className="input text-sm py-1.5 w-24"
                    value={newKeyForm.rate_limit}
                    onChange={e => setNewKeyForm(f => ({ ...f, rate_limit: parseInt(e.target.value) || 500 }))}
                    min={1}
                    max={10000}
                  />
                </div>
                <button type="submit" disabled={creatingKey} className="btn-primary py-1.5 text-sm">
                  {creatingKey ? 'Creando...' : 'Nueva key'}
                </button>
              </form>

              {/* Keys list */}
              {apiKeysLoading ? (
                <p className="text-sm text-gray-500">Cargando...</p>
              ) : apiKeys.length === 0 ? (
                <p className="text-sm text-gray-500">No hay API keys para esta organización.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                      <th className="pb-1">Nombre</th>
                      <th className="pb-1">Servicio</th>
                      <th className="pb-1">Prefijo</th>
                      <th className="pb-1">Rate limit</th>
                      <th className="pb-1">Último uso</th>
                      <th className="pb-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map((k: any) => (
                      <tr key={k._id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-1.5 pr-2 font-medium">{k.name}</td>
                        <td className="py-1.5 pr-2">
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{k.service || 'custom'}</span>
                        </td>
                        <td className="py-1.5 pr-2 font-mono text-xs text-gray-600">{k.key_prefix}...</td>
                        <td className="py-1.5 pr-2">{k.rate_limit}/15min</td>
                        <td className="py-1.5 pr-2 text-xs text-gray-500">
                          {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString('es') : '—'}
                        </td>
                        <td className="py-1.5">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleRegenerateApiKey(k._id, k.name)}
                              disabled={regeneratingKeyId === k._id}
                              className="text-xs text-vida-600 hover:text-vida-800 disabled:opacity-50"
                              title="Genera una clave nueva y la muestra una vez. La anterior deja de funcionar."
                            >
                              {regeneratingKeyId === k._id ? 'Regenerando…' : 'Regenerar'}
                            </button>
                            <button
                              onClick={() => handleRevokeApiKey(k._id)}
                              className="text-xs text-red-500 hover:text-red-700"
                              title="Revocar"
                            >
                              Revocar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedOrg(null);
                  setNewKeySecret(null);
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit modal */}
      <Modal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedOrg(null);
          setFormData({ name: '', slug: '', domain: '', welcome_text: '', allowed_origins: [], ecommerce: false, catalog: false, blog: false, multilingual: true, analytics: true, planner: false, processes: false });
          setEcommerceDbInfo(null);
        }}
        title="Editar organización"
        size="xl"
      >
        {/* Rejilla 2 columnas en escritorio (menos scroll), 1 en movil.
            Los bloques que necesitan ancho completo llevan md:col-span-2. */}
        <form onSubmit={(e) => { e.preventDefault(); handleEdit(); }} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          <div className="md:col-span-2">
            <label className="label">Nombre</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              onBlur={(e) => saveBasicField('name', e.target.value)}
              disabled={editLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Se guarda automáticamente al salir del campo.
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="label">Slug (no editable)</label>
            <input
              type="text"
              className="input font-mono bg-gray-100"
              value={formData.slug}
              disabled
            />
            <p className="mt-1 text-xs text-gray-500">
              El slug no se puede modificar una vez creada la organización
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="label">Dominio</label>
            <input
              type="text"
              className="input"
              placeholder="www.ejemplo.com"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              onBlur={(e) => saveBasicField('domain', e.target.value)}
              disabled={editLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Se guarda automáticamente al salir del campo.
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="label">Mensaje de bienvenida</label>
            <textarea
              className="input min-h-[80px]"
              placeholder="Texto que verán los miembros de esta organización al entrar al panel."
              value={formData.welcome_text}
              onChange={(e) => setFormData({ ...formData, welcome_text: e.target.value })}
              onBlur={(e) => saveBasicField('welcome_text', e.target.value)}
              disabled={editLoading}
              maxLength={2000}
            />
            <p className="mt-1 text-xs text-gray-500">
              Aparece arriba del panel. Útil para avisos permanentes o dar contexto a quien entra por
              primera vez. Se guarda automáticamente al salir del campo.
            </p>
          </div>

          {selectedOrg && (
            <PublicInfoEditor
              org={selectedOrg}
              onSaved={(org) => { setSelectedOrg(org); loadOrganizations(); }}
            />
          )}

          <div className="md:col-span-2">
            <label className="label">Orígenes permitidos (CORS)</label>
            <p className="mb-2 text-xs text-gray-500">
              Webs de esta organización que pueden llamar a la API desde el navegador.
              Añade el dominio de cada frontend (Vercel y dominio propio, si lo tiene).
              Sin esto, el navegador bloquea sus peticiones.
            </p>

            {formData.allowed_origins.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {formData.allowed_origins.map((origin) => (
                  <span
                    key={origin}
                    className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 py-1 pl-3 pr-1.5 font-mono text-xs text-gray-700"
                  >
                    {origin}
                    <button
                      type="button"
                      onClick={() => removeOrigin(origin)}
                      disabled={editLoading}
                      aria-label={`Quitar ${origin}`}
                      className="rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-red-600 disabled:opacity-50"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1 font-mono text-sm"
                placeholder="https://mi-web.vercel.app"
                value={originDraft}
                onChange={(e) => setOriginDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOrigin();
                  }
                }}
                disabled={editLoading}
              />
              <button
                type="button"
                className="btn btn-secondary whitespace-nowrap"
                onClick={addOrigin}
                disabled={editLoading || !originDraft.trim()}
              >
                Añadir
              </button>
            </div>
            {formData.allowed_origins.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Sin orígenes: los frontends de esta organización no podrán llamar a la
                API desde el navegador.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Ecommerce</label>
              <p className="text-sm text-gray-500">
                {formData.ecommerce
                  ? 'La tienda online está activa'
                  : 'Activa la tienda online para esta organización'}
              </p>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!selectedOrg) return;
                const newValue = !formData.ecommerce;
                setFormData({ ...formData, ecommerce: newValue });
                setEditLoading(true);
                try {
                  const updateData: any = {
                    name: formData.name,
                    config: {
                      features: {
                        ...(selectedOrg.config?.features || {}),
                        ecommerce: newValue,
                      },
                    },
                  };

                  await organizationsApi.update(selectedOrg._id, updateData);

                  if (newValue) {
                    try {
                      await organizationsApi.createEcommerceDb(selectedOrg._id);
                      toast.success('Ecommerce activado y base de datos creada');
                    } catch {
                      toast.success('Ecommerce activado');
                    }
                  } else {
                    toast.success('Ecommerce desactivado');
                  }

                  loadOrganizations();
                  const response = await organizationsApi.get(selectedOrg._id);
                  setSelectedOrg(response.data.organization);
                } catch (error: unknown) {
                  const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
                  toast.error(message);
                  setFormData({ ...formData, ecommerce: !newValue });
                } finally {
                  setEditLoading(false);
                }
              }}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.ecommerce ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.ecommerce ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {formData.ecommerce && (
            <div className="space-y-3 md:col-span-2">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Base de datos Ecommerce</p>
                    <p className="text-xs text-gray-500 font-mono">{`ecommerce_${selectedOrg?.slug || formData.slug}`}</p>
                  </div>
                  {hasPermission('*') && (
                    <button
                      onClick={handleResetEcommerceDb}
                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                    >
                      Reiniciar BD
                    </button>
                  )}
                </div>
              </div>

              {ecommerceDbInfo?.customersDbName && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">Base de datos Clientes (Batch)</p>
                      <p className="text-xs text-blue-600 font-mono">{ecommerceDbInfo.customersDbName}</p>
                      {ecommerceDbInfo.customerCount !== undefined && (
                        <p className="text-xs text-blue-600 mt-1">
                          {ecommerceDbInfo.customerCount} cliente{ecommerceDbInfo.customerCount !== 1 ? 's' : ''} sincronizado{ecommerceDbInfo.customerCount !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleSyncCustomers}
                      disabled={syncLoading}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {syncLoading ? 'Sincronizando...' : 'Sincronizar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Catálogo</label>
              <p className="text-sm text-gray-500">
                {formData.catalog
                  ? 'El catálogo de productos está activo'
                  : 'Activa el catálogo de productos para esta organización'}
              </p>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!selectedOrg) return;
                const newValue = !formData.catalog;
                setFormData({ ...formData, catalog: newValue });
                setEditLoading(true);
                try {
                  const updateData: any = {
                    name: formData.name,
                    config: {
                      features: {
                        ...(selectedOrg.config?.features || {}),
                        catalog: newValue,
                      },
                    },
                  };

                  await organizationsApi.update(selectedOrg._id, updateData);

                  if (newValue) {
                    toast.success('Catálogo activado');
                  } else {
                    toast.success('Catálogo desactivado');
                  }

                  loadOrganizations();
                  const response = await organizationsApi.get(selectedOrg._id);
                  setSelectedOrg(response.data.organization);
                } catch (error: unknown) {
                  const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
                  toast.error(message);
                  setFormData({ ...formData, catalog: !newValue });
                } finally {
                  setEditLoading(false);
                }
              }}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.catalog ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.catalog ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {formData.catalog && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 md:col-span-2">
              <div>
                <p className="text-sm font-medium text-purple-900">Base de datos Catálogo</p>
                <p className="text-xs text-purple-600 font-mono">{`catalog_${selectedOrg?.slug || formData.slug}`}</p>
              </div>
            </div>
          )}

          {/* Blog */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Blog</label>
              <p className="text-sm text-gray-500">
                {formData.blog
                  ? 'El blog está activo en esta organización'
                  : 'Activa el blog para esta organización'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleFeatureFlag('blog', 'Blog')}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.blog ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.blog ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Planificador */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Planificador</label>
              <p className="text-sm text-gray-500">
                {formData.planner
                  ? 'El módulo Planificador (recursos y servicios) está activo'
                  : 'Activa el módulo Planificador para esta organización'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleFeatureFlag('planner', 'Planificador')}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.planner ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.planner ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Autofichaje (VIDA-256): solo con el Planificador activo */}
          {formData.planner && (
            <>
          <div className="flex items-center justify-between py-3 border-t border-gray-200 pl-4">
            <div>
              <label className="label mb-1">Autofichaje</label>
              <p className="text-sm text-gray-500">
                {selfClockOn
                  ? 'Los trabajadores ligados a un recurso fichan su entrada y salida desde el panel.'
                  : 'Desactivado. Actívalo para que los trabajadores fichen desde el panel.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => togglePresenceFlag('self_clock_enabled', 'Autofichaje', !selfClockOn)}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                selfClockOn ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  selfClockOn ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <div className="flex items-center justify-between py-3 border-t border-gray-200 pl-4">
            <div>
              <label className="label mb-1">Ubicación al fichar</label>
              <p className="text-sm text-gray-500">
                {selfClockGeoOn
                  ? 'Se pide la ubicación del navegador al fichar. Informa a los trabajadores.'
                  : 'No se registra la ubicación, solo la hora y la IP.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => togglePresenceFlag('self_clock_geolocation', 'Ubicación al fichar', !selfClockGeoOn)}
              disabled={editLoading || !selfClockOn}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                selfClockGeoOn ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading || !selfClockOn ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  selfClockGeoOn ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
            </>
          )}

          {/* Procesos */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Procesos</label>
              <p className="text-sm text-gray-500">
                {formData.processes
                  ? 'El módulo Procesos (definición de procesos) está activo'
                  : 'Activa el módulo Procesos para esta organización'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleFeatureFlag('processes', 'Procesos')}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.processes ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.processes ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Bloque Gestion (Asociados / Cuotas / Presupuesto) — submodulos
              independientes con flag por organizacion. Cuando un flag esta
              undefined se trata como habilitado (legacy default). */}
          <div className="pt-4 pb-2 border-t border-gray-200 md:col-span-2">
            <p className="text-sm font-medium text-gray-700">Bloque Gestión</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Activa o desactiva por organización los módulos de Asociados, Cuotas y
              Presupuesto. Si están desactivados se ocultan del sidebar y la API responde
              403 al intentar usarlos.
            </p>
          </div>

          {/* Asociados */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Asociados</label>
              <p className="text-sm text-gray-500">
                {isManagementEnabled('members')
                  ? 'Directorio de miembros activado.'
                  : 'Módulo desactivado.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                toggleManagementFlag('members', 'Asociados', !isManagementEnabled('members'))
              }
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isManagementEnabled('members') ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isManagementEnabled('members') ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Cuotas */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Cuotas</label>
              <p className="text-sm text-gray-500">
                {isManagementEnabled('fees')
                  ? 'Planes y cobros de cuotas activados.'
                  : 'Módulo desactivado.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                toggleManagementFlag('fees', 'Cuotas', !isManagementEnabled('fees'))
              }
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isManagementEnabled('fees') ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isManagementEnabled('fees') ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Presupuesto */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Presupuesto</label>
              <p className="text-sm text-gray-500">
                {isManagementEnabled('budget')
                  ? 'Ejercicios, partidas y apuntes activados.'
                  : 'Módulo desactivado.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                toggleManagementFlag('budget', 'Presupuesto', !isManagementEnabled('budget'))
              }
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isManagementEnabled('budget') ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isManagementEnabled('budget') ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Reuniones IA (juntas con transcripción + resumen) — OPT-IN */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Reuniones (juntas con resumen IA)</label>
              <p className="text-sm text-gray-500">
                {isMeetingsEnabled()
                  ? 'Grabación, transcripción y resumen IA de reuniones activados.'
                  : 'Módulo desactivado. Actívalo para permitir su uso en esta organización.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                toggleManagementFlag('meetings', 'Reuniones', !isMeetingsEnabled())
              }
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isMeetingsEnabled() ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isMeetingsEnabled() ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* CRM (contactos multi-fuente, empresas, propuestas) — OPT-IN */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">CRM (contactos y propuestas)</label>
              <p className="text-sm text-gray-500">
                {isCrmEnabled()
                  ? 'Contactos multi-fuente, empresas y propuestas activados.'
                  : 'Módulo desactivado. Actívalo para permitir su uso en esta organización.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleManagementFlag('crm', 'CRM', !isCrmEnabled())}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isCrmEnabled() ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isCrmEnabled() ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Modo transparencia (solo si Presupuesto esta activo) */}
          {isManagementEnabled('budget') && (
            <div className="py-3 border-t border-gray-200 md:col-span-2">
              <label className="label mb-1">Transparencia del presupuesto</label>
              <p className="text-sm text-gray-500 mb-2">
                Decide si los socios con permiso de lectura ven los apuntes individuales o
                solo los totales agregados.
              </p>
              <select
                value={selectedOrg?.config?.management?.budget?.transparency ?? 'full'}
                onChange={(e) =>
                  setBudgetTransparency(e.target.value as 'full' | 'aggregated_only')
                }
                disabled={editLoading}
                className="input text-sm py-1.5"
              >
                <option value="full">
                  Completo — los socios ven KPIs y apuntes
                </option>
                <option value="aggregated_only">
                  Agregado — los socios solo ven KPIs (tesorería ve apuntes)
                </option>
              </select>
            </div>
          )}

          {/* Multilingual */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Multilingüe (ES/EN)</label>
              <p className="text-sm text-gray-500">
                {formData.multilingual
                  ? 'Los frontends muestran textos en ES y EN'
                  : 'Solo se mostrará el idioma por defecto'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleFeatureFlag('multilingual', 'Multilingüe')}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.multilingual ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.multilingual ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Analytics */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200">
            <div>
              <label className="label mb-1">Analítica</label>
              <p className="text-sm text-gray-500">
                {formData.analytics
                  ? 'Eventos de analítica enviados al backend'
                  : 'Sin recolección de eventos para esta organización'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleFeatureFlag('analytics', 'Analítica')}
              disabled={editLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.analytics ? 'bg-vida-600' : 'bg-gray-200'
              } ${editLoading ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.analytics ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Notificaciones (Módulo C) — canales de entrega de la org */}
          {selectedOrg && (
            <div className="py-3 border-t border-gray-200 md:col-span-2">
              <NotificationConfigEditor ref={notifRef} orgId={selectedOrg._id} slug={selectedOrg.slug} />
            </div>
          )}

          {/* Integración gk2control (control de presencia) */}
          {selectedOrg && (
            <div className="py-3 border-t border-gray-200 md:col-span-2">
              <Gk2ControlConfigEditor ref={gk2Ref} orgId={selectedOrg._id} slug={selectedOrg.slug} />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 md:col-span-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={resetEditModal}
              disabled={savingAll}
            >
              Cerrar
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={savingAll}
              onClick={async () => {
                // Guarda en paralelo notificaciones + gk2. El nombre, dominio y
                // toggles ya persisten en vivo (onBlur / click), asi que este
                // boton solo tiene que disparar los sub-editores.
                setSavingAll(true);
                try {
                  const results = await Promise.all([
                    notifRef.current?.save() ?? Promise.resolve(true),
                    gk2Ref.current?.save() ?? Promise.resolve(true),
                  ]);
                  if (results.every(Boolean)) {
                    resetEditModal();
                  }
                } finally {
                  setSavingAll(false);
                }
              }}
            >
              {savingAll ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Deploy info modal */}
      <Modal
        open={showDeployModal}
        onClose={() => {
          setShowDeployModal(false);
          setDeployInfo(null);
        }}
        title={`Ficha de despliegue${deployInfo ? ` — ${deployInfo.org.name}` : ''}`}
        size="xl"
      >
        {deployLoading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : deployInfo ? (
          <div className="space-y-4">
            {/* Public endpoints (always shown) */}
            {selectedOrg?.config?.features && (
              <div className="rounded-lg border border-gray-200 p-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Endpoints públicos</h4>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">Anuncios (web-publish) — actívalo en Notificaciones de la org</p>
                    <p className="text-xs font-mono text-gray-700 truncate">{announcementsEndpoint(selectedOrg.slug)}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(announcementsEndpoint(selectedOrg.slug))}
                    className="shrink-0 flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" /> Copiar
                  </button>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              {([
                { key: 'pet' as const, label: 'PET-VIDA / CMS' },
                { key: 'ecommerce' as const, label: 'Ecommerce' },
                { key: 'catalogo' as const, label: 'Catalogo' },
              ]).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setDeployTab(tab.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    deployTab === tab.key
                      ? 'border-vida-600 text-vida-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div>
              {deployTab === 'pet' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">vida_pet / .env.local</h4>
                    <button
                      onClick={() => copyToClipboard(deployInfo.vida_pet)}
                      className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                      Copiar
                    </button>
                  </div>
                  <pre className="p-4 bg-gray-900 text-green-400 text-xs font-mono rounded-lg overflow-x-auto max-h-80 whitespace-pre-wrap break-all">
                    {deployInfo.vida_pet}
                  </pre>
                  {renderDeployApiKeys('pet', 'pet')}
                </div>
              )}

              {deployTab === 'ecommerce' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">corestore/backend / .env</h4>
                    <button
                      onClick={() => copyToClipboard(deployInfo.ecommerce)}
                      className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                      Copiar
                    </button>
                  </div>
                  <pre className="p-4 bg-gray-900 text-green-400 text-xs font-mono rounded-lg overflow-x-auto max-h-80 whitespace-pre-wrap break-all">
                    {deployInfo.ecommerce}
                  </pre>
                  {renderDeployApiKeys('ecommerce', 'ecommerce')}
                </div>
              )}

              {deployTab === 'catalogo' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">vida_catalogo/backend / .env</h4>
                    <button
                      onClick={() => copyToClipboard(deployInfo.catalogo)}
                      className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                      Copiar
                    </button>
                  </div>
                  <pre className="p-4 bg-gray-900 text-green-400 text-xs font-mono rounded-lg overflow-x-auto max-h-80 whitespace-pre-wrap break-all">
                    {deployInfo.catalogo}
                  </pre>
                  {renderDeployApiKeys('catalog', 'catálogo')}
                </div>
              )}
            </div>

            {/* API URL section */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700">API URL: </span>
                  <span className="text-sm font-mono text-gray-600">{deployInfo.api_url}</span>
                </div>
                <button
                  onClick={() => copyToClipboard(deployInfo.api_url)}
                  className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                  Copiar
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowDeployModal(false);
                  setDeployInfo(null);
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Eliminar organización"
        message={`¿Estás seguro de eliminar "${selectedOrg?.name}"? Esta acción no se puede deshacer y se perderán todos los datos asociados.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}
